// frontend/src/pages/DemoLandingPage.js
// OGESTYLE landing — faithful React build of the approved Canva design
// (fonts: Fraunces display + DM Sans body; palette tokens from the Canva code).
// Primary CTA everywhere -> the live demo. Looks section uses a real OGESTYLE Look.
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import CustomerFooter from "../components/CustomerFooter";
import LandingSignupModal from "../components/LandingSignupModal";
import {
  FaBars,
  FaCalendarCheck,
  FaCalendarAlt,
  FaShoppingBag,
  FaChartLine,
  FaClipboardCheck,
  FaClock,
  FaMagic,
  FaHome,
  FaStore,
  FaCamera,
  FaArrowRight,
  FaSlidersH,
  FaCheckCircle,
  FaPlusCircle,
  FaMobileAlt,
  FaEnvelopeOpen,
  FaShieldAlt,
  FaGlobe,
  FaCreditCard,
  FaSyncAlt,
  FaUnlock,
  FaStar,
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaTwitter,
} from "react-icons/fa";
import "./DemoLandingPage.css";
import ProtectRevenue from "../components/ProtectRevenue";

const DEMO_URL = "https://demo.ogestyle.com";
const BOOKING_DEMO_URL = "https://www.ogestyle.com/demo";
const SUPPORT_EMAIL = "support@mg.ogestyle.com";

// Real OGESTYLE Look (middle / "Women's Haircut") used in the Looks section.
// All imagery is self-hosted under /landing (no Pexels hot-links).
const LOOK_IMG = "/landing/looks.jpg";

const IMG = {
  hero: "/landing/hero.jpg",
  salon: "/landing/salon.jpg",
  freelance: "/landing/freelance.jpg",
};

const NAV = [
  { label: "How it works", href: "#how-it-works" },
  { label: "For professionals", href: "#professionals" },
  { label: "Looks", href: "#looks" },
];

const BENEFITS = [
  { key: "book", icon: <FaCalendarCheck />, title: "Book & buy in one step", decor: "bag", bg: "white" },
  { key: "spend", icon: <FaChartLine />, title: "Higher spend per visit", decor: "bars", bg: "mist" },
  { key: "admin", icon: <FaClipboardCheck />, title: "Less admin for your team", decor: "clock", bg: "white" },
  { key: "looks", icon: <FaMagic />, title: "Looks", decor: "copy", bg: "mist" },
  { key: "everyone", icon: <FaHome />, title: "For everyone", decor: "houses", bg: "white" },
];

const STEPS = [
  { num: "01", title: "Client books service" },
  { num: "02", title: "Adds products in the same flow" },
  { num: "03", title: "Picks up at the appointment" },
];

const SERVICES = [
  { name: "Signature style", meta: "60 min", selected: true },
  { name: "Wash + finish", meta: "45 min", selected: false },
  { name: "Add a product", meta: "Pickup at appointment", selected: false },
];

export default function DemoLandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [picked, setPicked] = useState(0);

  // Refs used as scroll sentinels for the signup modals.
  const revenueRef = useRef(null);
  const afterPricingRef = useRef(null);

  // Signup ("Join OGESTYLE") always points at the real site so a demo visitor
  // who is ready to start lands on a real account, never a demo account.
  const isDemoHost =
    typeof window !== "undefined" &&
    /(^|\.)demo\.ogestyle\.com$/i.test(window.location.hostname);
  const SIGNUP_URL = isDemoHost ? "https://www.ogestyle.com/signup" : "/signup";
  // On www the walkthrough page is internal (/demo) so links navigate instantly;
  // on the demo host it must point to the real site.
  const BOOKING_URL = isDemoHost ? BOOKING_DEMO_URL : "/demo";

  return (
    <div className="lshell">
      {/* ── Header ── */}
      <header className="lheader">
        <div className="lwrap lnav">
          <Link to="/" className="llogo-link" aria-label="OGESTYLE home">
            <img src="/ogestyle-logo.png" alt="OGESTYLE" className="llogo" />
          </Link>
          <nav className="ldesktop-nav" aria-label="Primary navigation">
            {NAV.map((l) => (
              <a key={l.label} href={l.href} className="lnavlink">{l.label}</a>
            ))}
          </nav>
          <a href={DEMO_URL} target="_blank" rel="noreferrer noopener" className="lbtn lbtn-primary lheader-cta">
            See the demo
          </a>
          <button
            type="button"
            className="lmenu-btn"
            aria-label="Open navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <FaBars />
          </button>
        </div>
        <nav className={`lmobile-nav ${menuOpen ? "open" : ""}`} aria-label="Mobile navigation">
          {NAV.map((l) => (
            <a key={l.label} href={l.href} className="lnavlink" onClick={() => setMenuOpen(false)}>
              {l.label}
            </a>
          ))}
          <a href={DEMO_URL} target="_blank" rel="noreferrer noopener" className="lbtn lbtn-primary">
            See the demo
          </a>
        </nav>
      </header>

      {/* ── 1 · Hero ── */}
      <section className="lhero" id="top">
        <div className="ldots" />
        <div className="lwrap lhero-grid">
          <div className="fade-up">
            <p className="leyebrow">Beauty booking, made simpler</p>
            <h1 className="ldisplay">Book + Buy in One Step</h1>
            <p className="lhero-sub">
              The booking platform for salons, freelancers &amp; home-based
              beauty pros
            </p>
            <div className="lhero-ctas fade-up-late">
              <a href={DEMO_URL} target="_blank" rel="noreferrer noopener" className="lbtn lbtn-primary">
                See the demo
              </a>
              <a href="#how-it-works" className="llink-arrow">
                Explore how it works
              </a>
            </div>
          </div>

          <div className="ldevice-wrap">
            <span className="ldecor-ring" />
            <span className="ldecor-square" />
            <div className="ldevice">
              <div className="ldevice-head">
                <span className="ldevice-brand">OGESTYLE</span>
                <FaBars />
              </div>
              <img src={IMG.hero} alt="Professional hair stylist washing a client's hair in a salon" />
              <div className="lappt">
                <div className="lappt-row">
                  <div>
                    <p className="lappt-label">Your appointment</p>
                    <p className="lappt-title">Signature wash + style</p>
                  </div>
                  <span className="lappt-icon"><FaCalendarCheck /></span>
                </div>
                <div className="lappt-foot">
                  <span>Add products for pickup</span>
                  <span className="lappt-continue">Continue</span>
                </div>
              </div>
            </div>
            <div className="lbadge">
              <span className="lbadge-orb"><FaShoppingBag /></span>
              <span>One simple flow</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2 · Built for beauty ── */}
      <section className="lwrap lsec">
        <div className="lsec-head">
          <p className="leyebrow">Built for beauty</p>
          <h2 className="ldisplay">One place for the whole visit</h2>
        </div>
        <div className="lbenefits">
          {BENEFITS.map((b) => (
            <article className={`lcard benefit-card ${b.bg}`} key={b.key}>
              <span className="licon-orb">{b.icon}</span>
              <h3 className="ltitle">{b.title}</h3>
              {b.decor === "bag" && <FaShoppingBag className="ldecor-ic" />}
              {b.decor === "bars" && <span className="lbars" />}
              {b.decor === "clock" && <FaClock className="ldecor-ic" />}
              {b.decor === "copy" && <p className="lcard-copy">Clients book the style they see</p>}
              {b.decor === "houses" && (
                <span className="lhouses"><FaStore /><FaHome /></span>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* ── 3 · How it works (navy) ── */}
      <section className="lhow" id="how-it-works">
        <div className="lwrap">
          <p className="leyebrow light">How it works</p>
          <h2 className="ldisplay white">A smoother way to book</h2>
          <div className="lsteps">
            {STEPS.map((s) => (
              <article key={s.num}>
                <span className="lnum">{s.num}</span>
                <span className="lline" />
                <h3 className="lstep-title">{s.title}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4 · Looks (real OGESTYLE Look) ── */}
      <section className="lwrap llooks" id="looks">
        <div className="llooks-media">
          <span className="ldecor-blob" />
          <img className="lclip" src={LOOK_IMG} alt="A finished OGESTYLE Look — Women's Haircut" />
          <div className="lfinish-card">
            <span className="lfinish-icon"><FaCamera /></span>
            <div>
              <p className="lfinish-label">Finished look</p>
              <p className="lfinish-title">Now bookable</p>
            </div>
            <FaArrowRight className="lfinish-arrow" />
          </div>
        </div>
        <div className="llooks-copy">
          <p className="leyebrow">Looks</p>
          <h2 className="ldisplay">Turn your work into bookings</h2>
          <p className="llooks-desc">Clients book the style they see</p>
          <div className="llooks-note">
            <FaStore className="lnote-ic" />
            <span>From finished service to bookable style</span>
          </div>
          <a href={`${DEMO_URL}/looks/all`} target="_blank" rel="noreferrer noopener" className="llink-arrow looks-link">
            See real Looks in the demo
          </a>
        </div>
      </section>

      {/* ── 4b · Share your work (social) ── */}
      <section className="lsocial">
        <div className="lwrap lsocial-grid">
          <div className="lsocial-copy">
            <p className="leyebrow">Share your work</p>
            <h2 className="ldisplay">Your best looks, shared anywhere</h2>
            <p className="lsocial-desc">
              Every Look becomes a beautiful share card that clients and
              followers can post on Facebook, Instagram, WhatsApp and more — and
              every share links straight back to booking you.
            </p>
            <ul className="lsocial-list">
              <li><FaCheckCircle /> Shareable by anyone — not just you</li>
              <li><FaCheckCircle /> Every share links back to book</li>
              <li><FaCheckCircle /> Free exposure for your work</li>
            </ul>
            <div className="lsocial-chips">
              <span className="lsocial-chip"><FaFacebookF /> Facebook</span>
              <span className="lsocial-chip"><FaInstagram /> Instagram</span>
              <span className="lsocial-chip"><FaWhatsapp /> WhatsApp</span>
              <span className="lsocial-chip"><FaTwitter /> X</span>
            </div>
          </div>

          <div className="lsocial-mock">
            <img
              className="lsocial-shot"
              src="/landing/share-facebook.png"
              alt="A professional's OGESTYLE Look shared on Facebook"
            />
          </div>
        </div>
      </section>

      {/* ── 5 · For professionals ── */}
      <section className="lpros" id="professionals">
        <div className="lwrap">
          <div className="lpros-head">
            <p className="leyebrow">For professionals</p>
            <h2 className="ldisplay">Made for the way you work</h2>
          </div>
          <div className="lpros-grid">
            <article className="lpro-card white">
              <img src={IMG.salon} alt="Spacious modern hair salon" />
              <div className="lpro-body">
                <span className="licon-orb"><FaStore /></span>
                <h3 className="ltitle big">Salons &amp; studios</h3>
                <p className="lpro-desc">
                  Keep your team focused on great service with a connected
                  booking and product flow.
                </p>
              </div>
            </article>
            <article className="lpro-card navy">
              <img src={IMG.freelance} alt="Makeup artist at work in a beauty studio" />
              <div className="lpro-body">
                <span className="licon-orb light"><FaHome /></span>
                <h3 className="ltitle big white">Freelancers &amp; home-based</h3>
                <p className="lpro-desc white">
                  Give clients an easy way to discover your work, book
                  confidently, and plan their visit.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Revenue protection — scroll sentinel arms the first signup nudge */}
      <div ref={revenueRef} className="lscroll-anchor">
        <ProtectRevenue />
      </div>

      {/* ── 5b · Switching / migration + trial assurance ── */}
      <section className="lmigrate" id="switch">
        <div className="lwrap">
          <div className="lmigrate-head">
            <p className="leyebrow">Risk-free switch</p>
            <h2 className="ldisplay">Bring your bookings with you</h2>
            <p className="lmigrate-intro">
              Already running another booking system? Our migration wizard makes
              cutting over easy, and our team is here to help you through setup —
              nothing is lost, with 21 risk-free days to make the switch.
            </p>
          </div>

          <div className="lmigrate-grid">
            <article className="lmigrate-card">
              <span className="licon-orb"><FaClipboardCheck /></span>
              <span className="lmigrate-tag">Easy cutover</span>
              <h3 className="ltitle">Migration wizard makes it simple</h3>
              <p className="lmigrate-copy">
                Bring your shop across with a guided wizard — your pages,
                services and bookings are set up quickly while you keep running
                as usual.
              </p>
            </article>

            <article className="lmigrate-card">
              <span className="licon-orb"><FaCalendarAlt /></span>
              <span className="lmigrate-tag">Help when you need it</span>
              <h3 className="ltitle">Assistance during setup</h3>
              <p className="lmigrate-copy">
                Prefer a hand? Our team supports you through setup, so you never
                have to make the move alone. No booking information is lost.
              </p>
            </article>

            <article className="lmigrate-card">
              <span className="licon-orb"><FaCalendarCheck /></span>
              <span className="lmigrate-tag">Full 14-day trial</span>
              <h3 className="ltitle">Go live with confidence</h3>
              <p className="lmigrate-copy">
                21 risk-free days in total: 7 to set up and migrate, then 14
                full days to run and trial everything live.
              </p>
            </article>
          </div>

          <div className="lmigrate-foot">
            <span><FaCheckCircle /> No lost bookings</span>
            <span><FaCheckCircle /> No double entry</span>
            <span><FaCheckCircle /> No gap in service</span>
          </div>
        </div>
      </section>

      {/* ── 5c · Get started (owner steps) ── */}
      <section className="lstart">
        <div className="lwrap">
          <div className="lstart-head">
            <p className="leyebrow">Getting started is simple</p>
            <h2 className="ldisplay">Live in three easy steps</h2>
          </div>
          <div className="lstart-steps">
            <article className="lstart-step">
              <span className="lnum">01</span>
              <h3 className="ltitle">Register your business</h3>
              <p>
                Whether you&rsquo;re an owner, professional or booth renter, signup
                takes under 30 minutes.
              </p>
            </article>
            <article className="lstart-step">
              <span className="lnum">02</span>
              <h3 className="ltitle">Cut over easily</h3>
              <p>
                Our migration wizard makes switching fast and simple — you&rsquo;re
                in control. Brand new to online booking? You can start right
                away. We&rsquo;re available if you need a hand.
              </p>
            </article>
            <article className="lstart-step">
              <span className="lnum">03</span>
              <h3 className="ltitle">Go live on trial</h3>
              <p>
                Enjoy a full 14-day live trial. No contract &mdash; cancel
                anytime.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ── 6 · Product / demo mock ── */}
      <section className="lwrap ldemo" id="demo">
        <div>
          <p className="leyebrow">The product</p>
          <h2 className="ldisplay">See OGESTYLE in action</h2>
          <p className="ldemo-desc">A calm, connected experience for every booking.</p>
          <a href="#top" className="llink-arrow">Back to the top</a>
        </div>
        <div className="lbrowser">
          <div className="lbrowser-bar">
            <span /><span /><span />
            <div className="lbrowser-url" />
          </div>
          <div className="lbrowser-grid">
            <aside className="lsidenav">
              <p className="lsidenav-brand">OGESTYLE</p>
              <div className="lsidenav-items">
                <span><FaCalendarAlt /> Appointments</span>
                <span><FaMagic /> Looks</span>
                <span><FaShoppingBag /> Products</span>
              </div>
            </aside>
            <div className="lpanel">
              <div className="lpanel-head">
                <div>
                  <p className="lpanel-label">Book a service</p>
                  <p className="lpanel-title">Choose your appointment</p>
                </div>
                <FaSlidersH className="lpanel-slider" />
              </div>
              <div className="lservices">
                {SERVICES.map((s, i) => (
                  <button
                    type="button"
                    key={s.name}
                    className={`lservice ${picked === i ? "selected" : ""}`}
                    aria-pressed={picked === i}
                    onClick={() => setPicked(i)}
                  >
                    <span>
                      <span className="lservice-name">{s.name}</span>
                      <span className="lservice-meta">{s.meta}</span>
                    </span>
                    {picked === i ? <FaCheckCircle className="lservice-ok" /> : <FaPlusCircle className="lservice-plus" />}
                  </button>
                ))}
              </div>
              <div className="lcontinue">Continue booking</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6b · For your clients ── */}
      <section className="lwrap lsec lclients" id="clients">
        <div className="lclient-head">
          <p className="leyebrow">For your clients</p>
          <h2 className="ldisplay">No app. No friction. Just booking.</h2>
          <p className="lsec-sub">
            Your clients book straight from a link you share — nothing to
            download, no account to create.
          </p>
        </div>
        <div className="lclient-grid">
          <article className="lclient-card">
            <span className="licon-orb"><FaMobileAlt /></span>
            <div>
              <h3 className="ltitle">No app to download</h3>
              <p>Clients book from any device through one simple link — zero friction.</p>
            </div>
          </article>
          <article className="lclient-card">
            <span className="licon-orb"><FaEnvelopeOpen /></span>
            <div>
              <h3 className="ltitle">Email &amp; SMS reminders</h3>
              <p>Automatic reminders before every appointment help cut no-shows.</p>
            </div>
          </article>
          <article className="lclient-card">
            <span className="licon-orb"><FaShieldAlt /></span>
            <div>
              <h3 className="ltitle">Secure payments</h3>
              <p>Payments are processed securely by Stripe — card details are never stored.</p>
            </div>
          </article>
          <article className="lclient-card">
            <span className="licon-orb"><FaGlobe /></span>
            <div>
              <h3 className="ltitle">Keep your own website</h3>
              <p>Add your OGESTYLE booking link to your site. Booking &amp; product sales happen here.</p>
            </div>
          </article>
        </div>
      </section>

      {/* ── 6c · Pricing ── */}
      <section className="lpricing" id="pricing">
        <div className="lwrap">
          <div className="lpricing-head">
            <p className="leyebrow">Simple pricing</p>
            <h2 className="ldisplay">Honest pricing for every way you work</h2>
            <p className="lpricing-sub">
              One clear monthly rate — no hidden fees. The only extra cost is
              Stripe&rsquo;s standard processing fee.
            </p>
          </div>

          <div className="lplans">
            <article className="lplan navy">
              <span className="lplan-tag light">Salons, studios &amp; multi-location</span>
              <h3 className="lplan-name">Business</h3>
              <p className="lplan-price">$50<span className="lplan-per">/month</span></p>
              <p className="lplan-desc">
                One location — and every professional working in your shop is
                included.
              </p>
              <ul>
                <li><FaCheckCircle /> All your professionals covered</li>
                <li><FaCheckCircle /> Booth renters: +$20/month each (billed to the business)</li>
                <li><FaCheckCircle /> Extra locations: $45/month each</li>
                <li><FaCheckCircle /> Own dashboard &amp; insights for every pro and booth renter</li>
                <li><FaCheckCircle /> One dashboard for the whole shop</li>
              </ul>
            </article>

            <article className="lplan white">
              <span className="lplan-tag">Independent professionals</span>
              <h3 className="lplan-name">Freelancer</h3>
              <p className="lplan-price">$25<span className="lplan-per">/month</span></p>
              <p className="lplan-desc">
                For home-based and travel-to-client beauty professionals.
              </p>
              <ul>
                <li><FaCheckCircle /> Your own dashboard with metrics &amp; insights</li>
                <li><FaCheckCircle /> Discoverable profile &amp; looks</li>
                <li><FaCheckCircle /> Booking, products &amp; payments</li>
                <li><FaCheckCircle /> Email &amp; SMS reminders included</li>
                <li><FaCheckCircle /> No per-seat fees</li>
              </ul>
            </article>
          </div>

          <div className="lpricing-perk">
            <FaChartLine aria-hidden="true" />
            <p>
              You&rsquo;re not just getting a calendar to manage bookings — each
              professional, booth renter and freelancer gets their own dashboard
              with real metrics and insights into how they&rsquo;re performing.
            </p>
          </div>

          <div className="lpricing-facts">
            <div className="lpricing-fact">
              <span className="lpricing-fact-ic"><FaShoppingBag /></span>
              <div>
                <strong>Product fee</strong>
                <p>Small per-transaction fee on product sales.</p>
              </div>
            </div>
            <div className="lpricing-fact">
              <span className="lpricing-fact-ic"><FaCreditCard /></span>
              <div>
                <strong>Stripe fee</strong>
                <p>Standard 2.9% + $0.30 (US/CA) processing.</p>
              </div>
            </div>
            <div className="lpricing-fact">
              <span className="lpricing-fact-ic"><FaShieldAlt /></span>
              <div>
                <strong>No hidden fees</strong>
                <p>What you see is what you pay.</p>
              </div>
            </div>
            <div className="lpricing-fact">
              <span className="lpricing-fact-ic"><FaUnlock /></span>
              <div>
                <strong>No contract</strong>
                <p>Cancel anytime — no lock-in.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6d · Testimonial (placeholder — replace with a real quote) ── */}
      {/* Scroll sentinel: once the visitor has scrolled past Pricing, the
          second signup nudge fires. */}
      <section className="lwrap ltesti" ref={afterPricingRef}>
        <div className="ltesti-card">
          <div className="ltesti-stars" aria-hidden="true">
            <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
          </div>
          <blockquote className="ltesti-quote">
            &ldquo;We moved our whole salon over without losing a single booking.
            My clients book in seconds, and the automatic reminders have cut our
            no-shows.&rdquo;
          </blockquote>
          <div className="ltesti-attr">
            <span className="ltesti-avatar">SO</span>
            <div>
              <p className="ltesti-name">Salon owner</p>
              <p className="ltesti-role">Hair salon &amp; studio</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6e · Trust signals + help ── */}
      <section className="lwrap ltrust">
        <div className="ltrust-chips">
          <span className="ltrust-chip"><FaShieldAlt /> Payments secured by Stripe</span>
          <span className="ltrust-chip"><FaCreditCard /> Card details never stored</span>
          <span className="ltrust-chip"><FaClock /> Registration in under 30 minutes</span>
          <span className="ltrust-chip"><FaUnlock /> No contracts — cancel anytime</span>
          <span className="ltrust-chip"><FaSyncAlt /> New features shipped regularly</span>
        </div>
        <p className="ltrust-help">
          Questions? Our team is happy to help —{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="ltrust-link">{SUPPORT_EMAIL}</a>
        </p>
      </section>

      {/* ── 6f · FAQ ── */}
      <section className="lfaq" id="faq">
        <div className="lwrap">
          <div className="lfaq-head">
            <p className="leyebrow">Questions</p>
            <h2 className="ldisplay">Frequently asked questions</h2>
          </div>
          <div className="lfaq-list">
            <details className="lfaq-item" open>
              <summary>What happens to my current bookings if I switch?</summary>
              <p>
                Nothing is lost. Our migration wizard makes cutting over easy,
                and our team assists during setup if you need a hand. Existing
                and upcoming appointments are carried across for you — then you
                get a full 14-day trial.
              </p>
            </details>
            <details className="lfaq-item">
              <summary>Do my clients need to download an app?</summary>
              <p>
                No. Clients book from a simple link on any device — no download
                and no account required.
              </p>
            </details>
            <details className="lfaq-item">
              <summary>Can I keep using my own website?</summary>
              <p>
                Yes. Keep your current site and add your OGESTYLE booking link
                to it. Booking and product sales happen on OGESTYLE.
              </p>
            </details>
            <details className="lfaq-item">
              <summary>Are there contracts or hidden fees?</summary>
              <p>
                No contract — cancel anytime. There are no hidden fees; the only
                extra cost is Stripe&rsquo;s standard processing fee, plus a small
                per-transaction product fee on product sales.
              </p>
            </details>
            <details className="lfaq-item">
              <summary>How are payments and card details handled?</summary>
              <p>
                Payments are processed securely by Stripe. We never store card
                details.
              </p>
            </details>
            <details className="lfaq-item">
              <summary>How fast can I get started?</summary>
              <p>
                Registration takes under 30 minutes — and you can start taking
                bookings right away. Moving from another system, or simply want
                everything perfected first? Then we recommend using the first 7
                days to set up and migrate behind the scenes before your full
                14-day trial begins.
              </p>
            </details>
            <details className="lfaq-item">
              <summary>Will my clients get reminders?</summary>
              <p>
                Yes — automatic email and SMS reminders go out before
                appointments to help reduce no-shows.
              </p>
            </details>
            <details className="lfaq-item">
              <summary>How does pricing work for booth renters or multiple locations?</summary>
              <p>
                Business is $50/month and covers every professional in your
                shop. Booth renters rent their space from the business — you add
                them at $20/month each, billed to the business. Additional
                locations are $45/month each. Independent freelancers are
                $25/month.
              </p>
            </details>
          </div>
          <p className="lfaq-help">
            Still have questions?{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="ltrust-link">{SUPPORT_EMAIL}</a>{" "}
            or{" "}
            {isDemoHost ? (
              <a href={BOOKING_DEMO_URL} rel="noreferrer noopener" className="ltrust-link">
                book a live walkthrough
              </a>
            ) : (
              <Link to="/demo" className="ltrust-link">
                book a live walkthrough
              </Link>
            )}
            .
          </p>
        </div>
      </section>

      {/* ── 7 · Final CTA banner ── */}
      <section className="lwrap lbanner-wrap">
        <div className="lbanner">
          <div>
            <h2 className="ldisplay">See how it works</h2>
            <p className="lbanner-desc">Explore the OGESTYLE booking flow.</p>
          </div>
          <div className="lbanner-actions">
            <a href={DEMO_URL} target="_blank" rel="noreferrer noopener" className="lbtn lbtn-primary">
              Try the demo
            </a>
            {isDemoHost ? (
              <a href={BOOKING_DEMO_URL} rel="noreferrer noopener" className="lbtn lbtn-outline">
                Book a live walkthrough
              </a>
            ) : (
              <Link to="/demo" className="lbtn lbtn-outline">
                Book a live walkthrough
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ── Scroll-triggered signup nudges (this page only) ── */}
      {/* Nudge 1: fires as the "Protect your revenue" band scrolls into view. */}
      <LandingSignupModal
        triggerKey="protect-revenue"
        sentinelRef={revenueRef}
        eyebrow="Protect your revenue"
        title="Ready to stop losing money to no-shows?"
        body="You've seen the whole picture — booking &amp; products in one step, Looks your clients can book straight from social, and revenue protection that fills last-minute gaps. Create your real account and set up behind the scenes while you keep running as usual."
        note="21 risk-free days: 7 to set up &amp; migrate behind the scenes, then a full 14-day live trial. No contract — cancel anytime."
        icon={<FaShieldAlt />}
        primaryLabel="Start your free trial"
        primaryHref={SIGNUP_URL}
        secondaryLabel="Keep exploring"
      />

      {/* Nudge 2: fires after the visitor scrolls past Pricing. */}
      <LandingSignupModal
        triggerKey="after-pricing"
        sentinelRef={afterPricingRef}
        eyebrow="Simple pricing"
        title="Pick the plan that fits the way you work"
        body="Business at $50/month for salons &amp; studios — every professional in your shop included. Freelancer at $25/month for independents — no per-seat fees. One clear monthly rate, no hidden fees."
        note="21 risk-free days: 7 to set up &amp; migrate behind the scenes, then a full 14-day live trial. No contract — cancel anytime."
        icon={<FaCreditCard />}
        primaryLabel="Start your free trial"
        primaryHref={SIGNUP_URL}
        secondaryLabel="Book a live walkthrough"
        secondaryHref={BOOKING_URL}
      />

      {/* ── Standard site footer ── */}
      <CustomerFooter />
    </div>
  );
}
